import {
  type ChangeEvent,
  type ReactElement,
  type Ref,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';

type InputFileProps = {
  id: string;
  accept: string;
  onFileUpload: (file: File) => void;
  disabled?: boolean;
};

export type InputFileHandle = {
  clear: () => void;
};

export const InputFile = ({
  id,
  accept,
  onFileUpload,
  disabled,
  ref,
}: InputFileProps & {
  ref?: Ref<InputFileHandle>;
}): ReactElement => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');

  useImperativeHandle(ref, () => ({
    clear: () => {
      setFileName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
  }));

  const handleWrapperClick = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      onFileUpload(file);
    } else {
      setFileName('');
    }
    if (fileInputRef.current) {
      fileInputRef.current.blur();
    }
  };

  return (
    <div
      className={cn(
        'relative flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-md border border-border-default bg-layout px-3 py-2 text-layout-foreground text-sm',
        'focus-within:border-border-default focus-within:ring-2 focus-within:ring-primary-focus',
        disabled && 'cursor-not-allowed',
      )}
      onClick={handleWrapperClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleWrapperClick();
        }
      }}
      role='button'
      tabIndex={0}
    >
      <Button
        className='pr-0 pl-1 font-medium tracking-tight'
        disabled={disabled}
        onClick={handleWrapperClick}
        variant='ghost'
      >
        {t('Browse')}
      </Button>
      <Input
        accept={accept}
        aria-hidden='true'
        className={cn(
          'absolute inset-0 h-full w-full cursor-pointer opacity-0',
          disabled && 'hidden',
        )}
        disabled={disabled}
        id={id}
        onChange={handleChange}
        ref={fileInputRef}
        tabIndex={-1}
        type='file'
      />

      <span className={cn('truncate', disabled && 'opacity-50')}>
        {fileName || t('No file selected')}
      </span>
    </div>
  );
};

InputFile.displayName = 'InputFile';
